import React, { useState, useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import useScrollLock from '../../../hooks/useScrollLock';

interface EditNoteModalProps {
    open: boolean;
    initialText: string;
    onClose: () => void;
    onSave: (newText: string) => void;
}

const EditNoteModal: React.FC<EditNoteModalProps> = ({ open, initialText, onClose, onSave }) => {
    const [noteText, setNoteText] = useState(initialText);

    useScrollLock(open);

    useEffect(() => {
        setNoteText(initialText);
    }, [initialText]);

    const handleSave = () => {
        if (noteText.trim()) {
            onSave(noteText.trim());
            onClose();
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-[#FFF8F0] rounded-2xl p-6 sm:p-8 max-w-lg w-full border border-gray-200/60 shadow-xl">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-medium text-gray-900">Edit Note</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
                    >
                        <MdClose />
                    </button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Note Content
                        </label>
                        <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Write your note here..."
                            className="w-full px-4 py-3 bg-white border border-gray-200/60 rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 text-base min-h-[150px] resize-none"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.ctrlKey) {
                                    handleSave();
                                }
                            }}
                        />
                        <p className="text-xs text-gray-400 mt-2 text-right">
                            Press Ctrl+Enter to save
                        </p>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-3 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 rounded-xl font-medium transition-all duration-300 text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!noteText.trim() || noteText.trim() === initialText}
                            className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 text-sm ${!noteText.trim() || noteText.trim() === initialText
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                                }`}
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditNoteModal;
